import { useRef, useState } from "react";

import type { PaginatedResponse } from "@/api/cle-api";

import type { MockPagination } from "./types";

export interface DocumentPaginationTableProps {
  pagination: MockPagination;
  onPreviousPage: () => void;
  onNextPage: () => void;
}

export interface DocumentPagination {
  /** Sendes til listehooket - ændres af pilene og udløser dermed et nyt kald. */
  page: number;
  pageSize: number;
  /** De props tabellen skal have for at pilene både viser og virker. */
  tableProps: <T>(
    response?: PaginatedResponse<T>,
  ) => DocumentPaginationTableProps;
}

/**
 * Holder sidetallet for én dokumentliste på personens efteruddannelsesfane.
 *
 * Serverens totaler huskes i en ref, fordi hvert sidetal er sit eget query key: data er
 * `undefined` mens næste side hentes, og uden det ville footeren blinke "1 - 0 af 0" og
 * pilene blive slået fra midt i et klik-flow.
 */
export const useDocumentPagination = (pageSize: number): DocumentPagination => {
  const [page, setPage] = useState(1);
  const lastKnown = useRef({ total: 0, totalPages: 1 });

  return {
    page,
    pageSize,
    tableProps: (response) => {
      if (response) {
        lastKnown.current = {
          total: response.total,
          totalPages: response.totalPages,
        };
      }
      const { total, totalPages } = lastKnown.current;

      return {
        pagination: {
          page,
          pageSize,
          totalCount: total,
          hasPreviousPage: page > 1,
          hasNextPage: page < totalPages,
        },
        onPreviousPage: () => setPage((current) => Math.max(1, current - 1)),
        onNextPage: () =>
          setPage((current) => Math.min(totalPages, current + 1)),
      };
    },
  };
};
